import React, { useState, useRef, useCallback, useEffect } from "react";
import { X, Loader2, AlertCircle, Image, Info, Code, Edit3, Check } from "lucide-react";
import { Toolbar } from "./markdownEditorPageToolbar";
import { CodeEditor } from "./markdownEditorPageCodeEditor";
import MarkdownRenderer from "./markdownRenderer/MarkdownRenderer";
import TiptapEditor from "./TiptapEditor";
import { Button } from "../../@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";

// ============= R2 CONFIGURATION =============
const R2_CONFIG = {
  BUCKET_NAME: import.meta.env.VITE_BUCKET_NAME,
  ACCESS_KEY: import.meta.env.VITE_R2_ACCESS_KEY,
  SECRET_KEY: import.meta.env.VITE_R2_SECRET_KEY,
  ENDPOINT: import.meta.env.VITE_R2_ENDPOINT_URL,
  PUBLIC_URL: import.meta.env.VITE_R2_PUBLIC_URL,
};

// ============= CRYPTO HELPERS =============
const bufferToHex = (buffer) => {
  return Array.from(new Uint8Array(buffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
};

const hmacSHA256 = async (key, data) => {
  const encoder = new TextEncoder();
  const keyData = typeof key === "string" ? encoder.encode(key) : key;
  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    keyData,
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );
  const signature = await crypto.subtle.sign(
    "HMAC",
    cryptoKey,
    encoder.encode(data),
  );
  return new Uint8Array(signature);
};

const sha256Hex = async (data) => {
  const encoder = new TextEncoder();
  const dataToHash = typeof data === "string" ? encoder.encode(data) : data;
  const hash = await crypto.subtle.digest("SHA-256", dataToHash);
  return bufferToHex(hash);
};

const getSignatureKey = async (key, dateStamp, regionName, serviceName) => {
  const kDate = await hmacSHA256("AWS4" + key, dateStamp);
  const kRegion = await hmacSHA256(kDate, regionName);
  const kService = await hmacSHA256(kRegion, serviceName);
  return await hmacSHA256(kService, "aws4_request");
};

// === Markdown image helpers ===
const extractImageUrlsFromMarkdown = (text) => {
  if (!text) return [];
  const regex = /!\[[^\]]*\]\(([^)]+)\)/g;
  const urls = [];
  let match;
  while ((match = regex.exec(text)) !== null) {
    urls.push(match[1]);
  }
  return urls;
};

const derivePathFromUrl = (url) => {
  if (!url) return null;
  try {
    const publicBase = (R2_CONFIG.PUBLIC_URL || "").replace(/\/+$/g, "");
    if (publicBase && url.startsWith(publicBase)) {
      return url.slice(publicBase.length + 1).split("?")[0];
    }
    const bucket = R2_CONFIG.BUCKET_NAME || "";
    const bucketMarker = `/${bucket}/`;
    const idx = url.indexOf(bucketMarker);
    if (idx !== -1) {
      return url.slice(idx + bucketMarker.length).split("?")[0];
    }
  } catch (e) {
    // ignore
  }
  return null;
};

// ============= R2 UPLOAD FUNCTION =============
const uploadToR2 = async (file, contentFolder, contentName, imageOrder) => {
  try {
    const fileExtension = file.name.split(".").pop().toLowerCase();
    const slugify = (s) =>
      String(s || "")
        .toLowerCase()
        .trim()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9\-\_]/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "");

    const safeFolder = slugify(contentFolder);
    const safeName = slugify(contentName);
    const folderParts = safeFolder.split("-");
    const folderOrder = folderParts[0] || "1";

    const objectKey = `assets/images/${safeFolder}/${folderOrder}-${safeName}/${imageOrder}-image.${fileExtension}`;
    const fullPath = `/${R2_CONFIG.BUCKET_NAME}/${objectKey}`;

    const arrayBuffer = await file.arrayBuffer();
    const fileBytes = new Uint8Array(arrayBuffer);

    const payloadHash = await sha256Hex(fileBytes);

    const now = new Date();
    const amzDate = now.toISOString().replace(/[:-]|\.\d{3}/g, "");
    const dateStamp = amzDate.slice(0, 8);

    const host = new URL(R2_CONFIG.ENDPOINT).host;
    const region = "auto";
    const service = "s3";
    const algorithm = "AWS4-HMAC-SHA256";

    const canonicalUri = fullPath;
    const canonicalQuerystring = "";
    const canonicalHeaders = `host:${host}\nx-amz-content-sha256:${payloadHash}\nx-amz-date:${amzDate}\n`;
    const signedHeaders = "host;x-amz-content-sha256;x-amz-date";

    const canonicalRequest = [
      "PUT",
      canonicalUri,
      canonicalQuerystring,
      canonicalHeaders,
      signedHeaders,
      payloadHash,
    ].join("\n");

    const credentialScope = `${dateStamp}/${region}/${service}/aws4_request`;
    const canonicalRequestHash = await sha256Hex(canonicalRequest);
    const stringToSign = [
      algorithm,
      amzDate,
      credentialScope,
      canonicalRequestHash,
    ].join("\n");

    const signingKey = await getSignatureKey(
      R2_CONFIG.SECRET_KEY,
      dateStamp,
      region,
      service,
    );
    const signatureBytes = await crypto.subtle.sign(
      "HMAC",
      await crypto.subtle.importKey(
        "raw",
        signingKey,
        { name: "HMAC", hash: "SHA-256" },
        false,
        ["sign"],
      ),
      new TextEncoder().encode(stringToSign),
    );
    const signature = bufferToHex(signatureBytes);

    const authorizationHeader = `${algorithm} Credential=${R2_CONFIG.ACCESS_KEY}/${credentialScope}, SignedHeaders=${signedHeaders}, Signature=${signature}`;

    const url = `${R2_CONFIG.ENDPOINT}${fullPath}`;

    const response = await fetch(url, {
      method: "PUT",
      headers: {
        Host: host,
        Authorization: authorizationHeader,
        "x-amz-date": amzDate,
        "x-amz-content-sha256": payloadHash,
        "Content-Type": file.type || "image/jpeg",
        "Content-Length": fileBytes.length.toString(),
      },
      body: fileBytes,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Upload failed (${response.status}): ${errorText}`);
    }

    const ts = Date.now();
    const publicBase = (R2_CONFIG.PUBLIC_URL || "").replace(/\/+$/g, "");
    const publicUrl = `${publicBase}/${objectKey}?t=${ts}`;

    return {
      success: true,
      url: publicUrl,
      path: objectKey,
    };
  } catch (error) {
    console.error("R2 Upload Error:", error);
    return { success: false, error: error.message };
  }
};

const deleteFromR2 = async (path) => {
  try {
    const fullPath = `/${R2_CONFIG.BUCKET_NAME}/${path}`;
    const url = `${R2_CONFIG.ENDPOINT}${fullPath}`;

    const now = new Date();
    const amzDate = now.toISOString().replace(/[:-]|\.\d{3}/g, "");
    const dateStamp = amzDate.slice(0, 8);

    const host = new URL(R2_CONFIG.ENDPOINT).host;
    const region = "auto";
    const service = "s3";
    const algorithm = "AWS4-HMAC-SHA256";

    const payloadHash = await sha256Hex("");
    const canonicalUri = fullPath;
    const canonicalQuerystring = "";
    const canonicalHeaders = `host:${host}\nx-amz-content-sha256:${payloadHash}\nx-amz-date:${amzDate}\n`;
    const signedHeaders = "host;x-amz-content-sha256;x-amz-date";

    const canonicalRequest = [
      "DELETE",
      canonicalUri,
      canonicalQuerystring,
      canonicalHeaders,
      signedHeaders,
      payloadHash,
    ].join("\n");

    const credentialScope = `${dateStamp}/${region}/${service}/aws4_request`;
    const canonicalRequestHash = await sha256Hex(canonicalRequest);
    const stringToSign = [
      algorithm,
      amzDate,
      credentialScope,
      canonicalRequestHash,
    ].join("\n");

    const signingKey = await getSignatureKey(
      R2_CONFIG.SECRET_KEY,
      dateStamp,
      region,
      service,
    );
    const signatureBytes = await crypto.subtle.sign(
      "HMAC",
      await crypto.subtle.importKey(
        "raw",
        signingKey,
        { name: "HMAC", hash: "SHA-256" },
        false,
        ["sign"],
      ),
      new TextEncoder().encode(stringToSign),
    );
    const signature = bufferToHex(signatureBytes);

    const authorizationHeader = `${algorithm} Credential=${R2_CONFIG.ACCESS_KEY}/${credentialScope}, SignedHeaders=${signedHeaders}, Signature=${signature}`;

    const response = await fetch(url, {
      method: "DELETE",
      headers: {
        Host: host,
        Authorization: authorizationHeader,
        "x-amz-date": amzDate,
        "x-amz-content-sha256": payloadHash,
      },
    });

    return response.ok;
  } catch (error) {
    console.error("R2 Delete Error:", error);
    return false;
  }
};

// ============= PICKER SCREEN =============
function PickerScreen({ onPickTiptap, onPickRaw, onClose }) {
  const [selected, setSelected] = useState(null);

  const editorOptions = [
    {
      value: "tiptap",
      label: "Simple Editor",
      icon: Edit3,
      onClick: onPickTiptap,
    },
    {
      value: "raw",
      label: "Raw Markdown",
      icon: Code,
      onClick: onPickRaw,
    },
  ];

  const handleSelect = (option) => {
    setSelected(option.value);
    option.onClick();
  };

  return (
    <div className="flex-1 flex items-center justify-center bg-transparent rounded-lg relative">
      <div className="flex flex-col justify-center item-center rounded-lg gap-3 max-w-xs min-h-50 w-full">
        <label className="block font-semibold text-(--neutral-950) text-center">
          Choose Editor
        </label>
        <div className="grid grid-cols-2 gap-3">
          {editorOptions.map(({ value, label, icon: Icon, onClick }) => (
            <button
              key={value}
              onClick={() => handleSelect({ value, onClick })}
              className={`
                relative flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all
                ${
                  selected === value
                    ? "border-(--color-primary)"
                    : "border-(--neutral-200) hover:border-(--color-primary)"
                }
              `}
            >
              {selected === value && (
                <div className="absolute top-2 right-2 w-4 h-4 rounded-full flex items-center justify-center">
                  <Check className="w-3 h-3 text-(--color-primary)" />
                </div>
              )}
              <Icon
                className={`w-5 h-5 ${selected === value ? "text-(--color-primary)" : "text-(--neutral-600)"}`}
              />
              <span
                className={`text-sm font-medium ${selected === value ? "text-(--color-primary)" : "text-(--neutral-700)"}`}
              >
                {label}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============= MAIN DIALOG =============
export default function MarkdownEditorDialog({
  open,
  initialValue = "",
  onClose,
  onSave,
  contentFolder = "1-lessons",
  contentName = "introduction",
}) {
  const [content, setContent] = useState(initialValue);
  const [viewMode, setViewMode] = useState("split");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [imageCounter, setImageCounter] = useState(1);
  const [uploadError, setUploadError] = useState(null);
  const [editorMode, setEditorMode] = useState("picker"); // "picker" | "tiptap" | "raw"
  const [brokenImageUrls, setBrokenImageUrls] = useState([]);
  const textareaRef = useRef(null);

  // Sync content and reset dialog state when opening
  const wasOpenRef = useRef(false);
  useEffect(() => {
    const wasOpen = wasOpenRef.current;
    if (open && !wasOpen) {
      setContent(initialValue);
      setEditorMode("picker");
      setBrokenImageUrls([]);
      setUploadError(null);
    }
    if (!open) {
      setContent(initialValue);
    }
    wasOpenRef.current = open;
  }, [open, initialValue]);

  // Populate uploadedImages from content when dialog opens
  useEffect(() => {
    if (!open) return;
    const text = initialValue || "";
    const urls = extractImageUrlsFromMarkdown(text);
    if (urls.length === 0) {
      setUploadedImages([]);
      setImageCounter(1);
      return;
    }
    const parsed = urls.map((u, idx) => ({
      url: u,
      path: derivePathFromUrl(u),
      order: idx + 1,
    }));
    setUploadedImages(parsed);
    setImageCounter(parsed.length + 1);
  }, [open, initialValue]);

  // Detect broken images in content (skips images uploaded this session)
  useEffect(() => {
    if (!content || editorMode === "picker") return;
    const urls = extractImageUrlsFromMarkdown(content);
    const unknownUrls = urls.filter(
      (url) => !uploadedImages.some((i) => i.url === url),
    );
    if (unknownUrls.length === 0) {
      setBrokenImageUrls([]);
      return;
    }
    const checkUrl = (url) =>
      new Promise((resolve) => {
        const img = new window.Image();
        img.onload = () => resolve(null);
        img.onerror = () => resolve(url);
        img.src = url;
      });
    Promise.all(unknownUrls.map(checkUrl)).then((results) => {
      setBrokenImageUrls(results.filter(Boolean));
    });
  }, [content, editorMode, uploadedImages]);

  const handleImageUpload = useCallback(
    async (e, options = {}) => {
      const files = Array.from(e?.target?.files || []);
      if (files.length === 0) return [];

      setIsUploading(true);
      setUploadError(null);

      try {
        let localCounter = imageCounter;
        const uploaded = [];

        for (const file of files) {
          const order = localCounter;
          const result = await uploadToR2(
            file,
            contentFolder,
            contentName,
            order,
          );
          if (result.success) {
            const imgEntry = { path: result.path, url: result.url, order };
            uploaded.push(imgEntry);
            setUploadedImages((prev) => [...prev, imgEntry]);
            localCounter += 1;
          } else {
            setUploadError(result.error);
          }
        }
        setImageCounter(localCounter);

        // Tiptap mode: return uploaded list so TiptapEditor inserts them
        if (options?.mode === "tiptap") return uploaded;

        // Raw mode: insert markdown at Monaco cursor
        const editor = textareaRef.current;
        for (const img of uploaded) {
          const imageMarkdown = `![Image ${img.order}](${img.url})`;
          if (editor) {
            const pos = editor.getPosition();
            const range = new monaco.Range(
              pos.lineNumber,
              pos.column,
              pos.lineNumber,
              pos.column,
            );
            editor.executeEdits("image-upload", [
              { range, text: imageMarkdown + "\n" },
            ]);
          }
        }
        if (editor) {
          setContent(editor.getValue());
          setTimeout(() => editor.focus(), 0);
        }
        return uploaded;
      } catch (error) {
        setUploadError(error.message);
        return [];
      } finally {
        setIsUploading(false);
        if (e?.target) e.target.value = "";
      }
    },
    [contentFolder, contentName, imageCounter],
  );

  const handleDeleteImage = useCallback(async (img) => {
    setUploadedImages((prev) => prev.filter((i) => i.path !== img.path));

    const editor = textareaRef.current;
    if (editor) {
      const value = editor.getValue();
      let newValue = value;

      if (img.path) {
        const escapedPath = img.path.replace(/[.*+?^${}()|[\\]\\]/g, "\\$&");
        const pathRegex = new RegExp(
          `!\\[[^\\]]*\\]\\([^\\)]*${escapedPath}[^\\)]*\\)\\n?`,
          "g",
        );
        newValue = value.replace(pathRegex, "");
      } else if (img.url) {
        const escaped = img.url.replace(/[.*+?^${}()|[\\]\\]/g, "\\$&");
        const regex = new RegExp(`!\\[[^\\]]*\\](${escaped})\\n?`, "g");
        newValue = value.replace(regex, "");
      }

      if (newValue !== value) {
        editor.setValue(newValue);
        setContent(newValue);
      }
    } else {
      // Tiptap mode — remove from content string
      if (img.url) {
        const escaped = img.url.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        setContent((prev) =>
          prev.replace(
            new RegExp(`!\\[[^\\]]*\\]\\(${escaped}\\)\\n?`, "g"),
            "",
          ),
        );
      }
    }

    try {
      if (img?.path) {
        await deleteFromR2(img.path);
      }
    } catch (err) {
      console.error(err);
      setUploadError(err.message || "Failed to delete image");
    }
  }, []);

  // Called by TiptapEditor's onDeleteImage(url) callback
  const handleDeleteImageByUrl = useCallback(
    async (url) => {
      const img = uploadedImages.find((i) => i.url === url);
      if (img) {
        await handleDeleteImage(img);
      } else {
        const path = derivePathFromUrl(url);
        if (path) {
          setContent((prev) => {
            const escaped = url.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
            return prev.replace(
              new RegExp(`!\\[[^\\]]*\\]\\(${escaped}\\)\\n?`, "g"),
              "",
            );
          });
          await deleteFromR2(path);
        }
      }
    },
    [uploadedImages, handleDeleteImage],
  );

  // Delete images that were uploaded but are no longer referenced in content
  const syncOrphans = useCallback(async () => {
    const contentUrls = new Set(extractImageUrlsFromMarkdown(content));
    const orphans = uploadedImages.filter(
      (img) => img.url && !contentUrls.has(img.url),
    );
    for (const orphan of orphans) {
      if (orphan.path) await deleteFromR2(orphan.path);
    }
    if (orphans.length > 0) {
      setUploadedImages((prev) =>
        prev.filter((img) => !orphans.some((o) => o.path === img.path)),
      );
    }
  }, [content, uploadedImages]);

  const handleInsert = useCallback(
    (format) => {
      const editor = textareaRef.current;
      if (!editor) return;

      const selection = editor.getSelection();
      const model = editor.getModel();
      const selectedText = model.getValueInRange(selection);

      if (format && typeof format === "object" && format.action) {
        editor.executeEdits("table-insert", [
          { range: selection, text: format.action },
        ]);
        setContent(editor.getValue());
        editor.focus();
        return;
      }

      const before = format?.before || "";
      const after = format?.after || "";
      const placeholder = format?.placeholder || "";

      let insertText;
      let newSelection;

      if (selectedText) {
        insertText = before + selectedText + after;
        newSelection = new monaco.Selection(
          selection.endLineNumber,
          selection.endColumn + before.length + after.length,
          selection.endLineNumber,
          selection.endColumn + before.length + after.length,
        );
      } else {
        insertText = before + placeholder + after;
        const startPosition = editor.getPosition();
        newSelection = new monaco.Selection(
          startPosition.lineNumber,
          startPosition.column + before.length,
          startPosition.lineNumber,
          startPosition.column + before.length + placeholder.length,
        );
      }

      editor.executeEdits("format-insert", [
        { range: selection, text: insertText },
      ]);
      setContent(editor.getValue());
      editor.focus();

      setTimeout(() => {
        editor.setSelection(newSelection);
      }, 0);
    },
    [content],
  );

  return (
    <Dialog open={open} onOpenChange={onClose} modal={true}>
      <DialogContent
        className={`z-9999 border border-(--neutral-300) p-0 flex flex-col bg-(--surface-base) text-(--neutral-900) rounded-lg ${
          editorMode === "picker"
            ? "max-w-md h-auto"
            : "max-w-[90vw] h-[96vh]"
        }`}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => {
          e.stopPropagation();
          onClose();
        }}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        {/* Header — hidden in picker mode */}
        {editorMode !== "picker" && (
          <div className="flex items-center justify-between p-4 border-b border-(--neutral-300)">
            <h2 className="text-lg font-bold text-(--neutral-900)">
              Markdown Editor
            </h2>
          </div>
        )}

        {/* Toolbar — raw mode only */}
        {editorMode === "raw" && (
          <Toolbar
            onInsert={handleInsert}
            viewMode={viewMode}
            setViewMode={setViewMode}
            onImageUpload={handleImageUpload}
            isUploading={isUploading}
          />
        )}

        {/* Upload error */}
        {uploadError && (
          <div className="border-b border-(--color-primary) px-4 py-2 flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-(--color-primary)" />
            <p className="text-sm text-(--color-primary)">
              Upload failed: {uploadError}
            </p>
            <button
              onClick={() => setUploadError(null)}
              className="ml-auto text-(--color-primary) hover:text-(--color-accent)"
            >
              ×
            </button>
          </div>
        )}

        {/* Broken image warning */}
        {brokenImageUrls.length > 0 && (
          <div className="border-b border-amber-400 px-4 py-2 flex items-start gap-2 bg-amber-50 dark:bg-amber-950">
            <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
            <p className="text-sm text-amber-700 dark:text-amber-300">
              {brokenImageUrls.length} image
              {brokenImageUrls.length !== 1 ? "s" : ""} in this content may be
              broken or unavailable.
            </p>
          </div>
        )}

        {/* Main content area */}
        <div className="flex-1 flex overflow-hidden">
          {editorMode === "picker" && (
            <PickerScreen
              onPickTiptap={() => setEditorMode("tiptap")}
              onPickRaw={() => setEditorMode("raw")}
              onClose={onClose}
            />
          )}

          {editorMode === "tiptap" && (
            <TiptapEditor
              value={content}
              onChange={setContent}
              onImageUpload={handleImageUpload}
              onDeleteImage={handleDeleteImageByUrl}
              isUploading={isUploading}
            />
          )}

          {editorMode === "raw" && (
            <>
              {(viewMode === "edit" || viewMode === "split") && (
                <div
                  className={`${
                    viewMode === "split" ? "w-1/2" : "w-full"
                  } border-r border-(--neutral-300)`}
                >
                  <CodeEditor
                    value={content}
                    onChange={(value) => setContent(value)}
                    placeholder="Start typing your markdown here..."
                    onInsert={handleInsert}
                    onMount={(editor) => (textareaRef.current = editor)}
                  />
                </div>
              )}
              {(viewMode === "preview" || viewMode === "split") && (
                <div
                  className={`${
                    viewMode === "split" ? "w-1/2" : "w-full"
                  } overflow-y-auto`}
                >
                  <div className="p-8 max-w-none prose dark:prose-invert">
                    <MarkdownRenderer content={content} />
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer — hidden in picker mode */}
        {editorMode !== "picker" && (
          <div className="flex justify-between items-center gap-3 p-4 border-t border-(--neutral-300)">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setEditorMode("picker")}
                className="text-xs text-(--neutral-500) hover:text-(--neutral-700) underline"
              >
                Switch editor
              </button>
              {uploadedImages.length > 0 && (
                <p className="text-xs text-(--neutral-600)">
                  Uploaded {uploadedImages.length} image
                  {uploadedImages.length !== 1 ? "s" : ""} to R2
                </p>
              )}
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  await syncOrphans();
                  onSave(content);
                }}
              >
                Save
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
